import { useEffect, useState, type FormEvent } from "react";

import FaqWrapper from "react-faq-component";
import { useToasts } from "react-toast-notifications";

import { FETCH_SETTINGS_DETAILS } from "@/queries";
import { useUser } from "@components/user-context";
import { useNorthbeamMutation } from "@hooks/http";
import {
  AttemptToAddAccountManagerStatus,
  type AttemptToAddAccountManager,
  type AttemptToAddAccountManagerVariables,
  type AttemptToRemoveAccountManager,
  type AttemptToRemoveAccountManagerVariables,
  type FetchSettingsDetails_me_managerUsers,
} from "@north-beam/nb-client-side";
import {
  type ATTEMPT_TO_ADD_ACCOUNT_MANAGER_STATUS,
  type BulkEmailVerifiedResponse,
} from "@north-beam/nb-common";
import { PrimaryButton } from "@shared/buttons";
import { TextInput } from "@shared/text-input";

import {
  AccountManagersTableV2,
  type ManagerUser,
} from "./account-managers-table-v2";
import {
  ATTEMPT_TO_ADD_ACCOUNT_MANAGER,
  ATTEMPT_TO_REMOVE_ACCOUNT_MANAGER,
} from "./queries";

const styles = {
  bgColor: "white",
  rowContentTextSize: "16px",
  rowContentPaddingTop: "10px",
  rowContentPaddingBottom: "10px",
  rowContentPaddingLeft: "20px",
  rowContentPaddingRight: "20px",
  arrowColor: "black",
};

const config = {
  tabFocus: true,
};

const faqData = {
  rows: [
    {
      title: "Why should I invite account managers?",
      content:
        "Account managers are able to assist with onboarding tasks that you may be unable to complete on your own.",
    },
    {
      title: "Who should I invite?",
      content: (
        <ol
          style={{ listStyleType: "disc" }}
          className="list-inside m-0 space-y-2 marker:text-xxs"
        >
          <li>
            We recommend adding any stakeholders who are interested in Northbeam
            data. This may include ad buyers/managers, executives, and agency
            partners.
          </li>
          <li>
            If you work with external partners for platform-specific advertising
            you’ll want to collect login credentials from them or add them as an
            account manager.
          </li>
          <li>
            You may want to add an engineer to assist with implementing the
            Northbeam pixel on your site and setting up the Northbeam Orders
            API.
          </li>
        </ol>
      ),
    },
    {
      title: "What permissions do account managers have?",
      content: `Account managers are able to add other managers, view all data, view and set connections, and perform every action that the account owner can perform.
        They cannot delete other account managers. This right is reserved for the admin account manager of your account.`,
    },
    {
      title: "What should I do if the invitee does not receive an email?",
      content: `If the user does not receive a email, please check the spam folder.
            If they still do not receive an email, they can generate a password
            through the ’password reset flow’.`,
    },
  ],
};

export const AccountManagerSectionV2 = (props: {
  managerUsers: FetchSettingsDetails_me_managerUsers[];
}) => {
  const { user } = useUser();
  const isAccountOwner = user.isAdmin || !user.impersonatedUser;

  const dataUsersToManagerUsers = (): ManagerUser[] => {
    return (
      props.managerUsers.map((user) => {
        return {
          id: user.id,
          email: user.email,
          isAdmin: user.isAdmin,
          status: "loading",
        };
      }) || []
    );
  };

  const [managerUsers, setManagerUsers] = useState<ManagerUser[]>(
    dataUsersToManagerUsers(),
  );

  useEffect(() => {
    const getManagerEmailVerificationStatus = async () => {
      const managerUsers = dataUsersToManagerUsers();
      // then do the magic
      try {
        const emailVerificationResult = await user.callApi<{
          data: BulkEmailVerifiedResponse;
        }>("/api/user/bulk-email-verified-status", {
          method: "POST",
          data: {
            userIds: managerUsers.map((i) => i.id),
          },
        });

        managerUsers.forEach((user) => {
          const verifiedUser = emailVerificationResult.data.find(
            ({ userId }) => user.id === userId,
          );

          if (verifiedUser && verifiedUser.isFound) {
            user.status = verifiedUser.isActive ? "verified" : "not-verified";
          } else {
            user.status = "error";
          }
        });
        setManagerUsers(managerUsers);
      } catch (err) {
        managerUsers.forEach((user) => (user.status = "error"));
        setManagerUsers(managerUsers);
      }
    };

    setManagerUsers(dataUsersToManagerUsers());
    getManagerEmailVerificationStatus();
  }, [props.managerUsers, user]);

  const [addManagerStatus, setAddManagerStatus] =
    useState<ATTEMPT_TO_ADD_ACCOUNT_MANAGER_STATUS | null>(null);
  const [attemptToAddAccountManager, { loading: addManagerLoading }] =
    useNorthbeamMutation<
      AttemptToAddAccountManager,
      AttemptToAddAccountManagerVariables
    >(ATTEMPT_TO_ADD_ACCOUNT_MANAGER, {
      refetchQueries: [{ query: FETCH_SETTINGS_DETAILS }],
    });

  const addManager = async (emailAddress: string, isResend?: boolean) => {
    setAddManagerStatus(null);

    try {
      const result = await attemptToAddAccountManager({
        variables: { email: emailAddress },
      });

      if (result?.data?.attemptToAddAccountManager) {
        if (
          isResend &&
          result.data.attemptToAddAccountManager === "AlreadyManager"
        ) {
          setAddManagerStatus("InvitationSent");
        } else {
          setAddManagerStatus(result.data.attemptToAddAccountManager);
        }
      } else {
        setAddManagerStatus(AttemptToAddAccountManagerStatus.HasError);
      }
    } catch (e) {
      setAddManagerStatus(AttemptToAddAccountManagerStatus.HasError);
    }
  };

  const [attemptToRemoveAccountManager, { loading: removeManagerLoading }] =
    useNorthbeamMutation<
      AttemptToRemoveAccountManager,
      AttemptToRemoveAccountManagerVariables
    >(ATTEMPT_TO_REMOVE_ACCOUNT_MANAGER, {
      refetchQueries: [{ query: FETCH_SETTINGS_DETAILS }],
    });

  const canRemoveManager = (id: string): boolean => {
    const managerUser = managerUsers.find((user) => user.id === id);
    if (!managerUser) {
      return false;
    }
    return (
      isAccountOwner &&
      managerUser.email !== user.email &&
      managerUser.id !== user.displayUserId
    );
  };
  const removeManager = async (id: string) => {
    if (!canRemoveManager(id)) {
      return;
    }
    setManagerUsers(managerUsers.filter((user) => user.id !== id));
    await attemptToRemoveAccountManager({
      variables: { managerId: id },
    });
  };

  const [newManagerEmail, setNewManagerEmail] = useState<string>("");

  const { addToast } = useToasts();
  useEffect(() => {
    if (addManagerLoading || !addManagerStatus) {
      return;
    }

    switch (addManagerStatus) {
      case "InvitationSent":
      case "SuccessfullyAdded":
        addToast(`Invite sent to ${newManagerEmail}`, {
          appearance: "success",
        });
        break;
      case "AlreadyManager":
        addToast(`User ${newManagerEmail} already exists`, {
          appearance: "warning",
        });
        break;
      default:
        console.warn("Add manager error:", addManagerStatus);
        addToast(`Failed to invite ${newManagerEmail}`, {
          appearance: "error",
        });
    }
  }, [addManagerLoading, addManagerStatus]);
  return (
    <>
      <div
        className="
  [&_h1]:text-2xl [&_h1]:font-semibold [&_h1]:pb-2
  [&_p]:p-0 [&_p]:m-0
  "
      >
        <div className="w-full p-10 pb-20 inline-flex flex-col gap-6">
          <div>
            <h1>Invite account managers to join your team</h1>
            <p className="text-lg leading-normal">
              Account managers are able to assist with onboarding tasks that you
              may be unable to complete on your own.
            </p>
          </div>
          <form
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              addManager(newManagerEmail);
            }}
            className="flex items-end space-x-4"
          >
            <div className="w-[443px]">
              <TextInput
                label="Email"
                value={newManagerEmail}
                placeholder="e.g. steve@apple.com"
                onChange={({ target }) =>
                  setNewManagerEmail(target.value.toLocaleLowerCase().trim())
                }
              />
            </div>
            <PrimaryButton
              disabled={!newManagerEmail}
              loading={addManagerLoading}
              type="submit"
              className="w-[137px] disabled:bg-gray"
            >
              {addManagerLoading ? "Please Wait..." : "SEND INVITE"}
            </PrimaryButton>
          </form>
          <PeopleToInviteInfoPanel />
        </div>
        <div className="w-full p-10 pb-20 inline-flex flex-col gap-6 border-t border-gray-1">
          <h1>Existing invitations</h1>
          <AccountManagersTableV2
            users={managerUsers}
            removeManager={removeManager}
            canRemove={canRemoveManager}
            resendInvite={(emailAddress: string) =>
              addManager(emailAddress, true)
            }
            resendInviteLoading={addManagerLoading}
          />
        </div>
      </div>
      <div className="px-10 pb-[80px] border-t border-gray-0">
        <div className="text-2xl font-semibold leading-loose pt-10">
          Frequently asked
        </div>
        <FaqWrapper data={faqData} styles={styles} config={config} />
      </div>
    </>
  );
};

const PeopleToInviteInfoPanel = () => {
  return (
    <div
      className="w-full px-8 py-6 border rounded-lg flex flex-col gap-6
  [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:m-0 [&_h3]:p-0
  [&_p]:text-xs [&_p]:leading-[14px]
  "
    >
      <h2 className="m-0 p-0 text-xl font-semibold">
        People you’ll want to invite
      </h2>
      <div className="w-full px-4 flex justify-between overflow-auto">
        {[
          {
            tshirtColor: "blue",
            platformIcon: <FacebookIcon />,
            title: "Ad account owners",
            text: <p className="w-[79px]">(e.g. Facebook Ads Manager)</p>,
          },
          {
            tshirtColor: "mint",
            platformIcon: <GodaddyIcon />,
            title: "DNS owner",
            text: (
              <p className="w-[141px]">
                (e.g. Godaddy, Google Domains, Cloudflare)
              </p>
            ),
          },
          {
            tshirtColor: "earth",
            platformIcon: <ShopifyIcon />,
            title: "E-commerce owner",
            text: (
              <p className="w-[130px]">(e.g. Shopify, BigCommece, Woo, etc.)</p>
            ),
          },
        ].map((info) => (
          <div key={info.title} className="flex gap-4">
            <div className="relative flex justify-center">
              <PersonIcon shirtColor={info.tshirtColor as shirtColorOptions} />
              <div className="w-20 h-20 absolute rounded-[168.42px] border border-black" />
              <div className="mb-1 w-5 h-5 bottom-0 absolute bg-cosmic+2 rounded-full">
                {info.platformIcon}
              </div>
            </div>
            <div className="flex flex-col gap-0.5 justify-center items-start">
              <h3>{info.title}</h3>
              {info.text}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* eslint-disable max-len */

type shirtColorOptions = "blue" | "mint" | "earth";

function shirtColorHex(shirtColor: shirtColorOptions) {
  switch (shirtColor) {
    case "blue":
      return "#F3F4FF";
    case "mint":
      return "#DDFAFA";
    case "earth":
      return "#E9F2D8";
  }

  return "#F3F4FF";
}

const PersonIcon = ({ shirtColor }: { shirtColor: shirtColorOptions }) => (
  <svg
    width="80"
    height="80"
    viewBox="0 0 80 80"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="80" height="80" rx="40" fill="#EAECFF" />
    <mask
      id="mask0_2409_199502"
      style={{ maskType: "alpha" }}
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="80"
      height="80"
    >
      <rect width="80" height="80" rx="40" fill="#EAECFF" />
    </mask>
    <g mask="url(#mask0_2409_199502)">
      <path
        /* eslint-disable max-len */
        d="M73.325 81.2746C73.3064 81.256 72.2105 75.182 71.5418 67.5664C71.0403 61.8267 68.9041 57.2388 67.6782 55.0284C67.1024 53.9882 66.1179 53.208 64.9849 52.8923C60.6384 51.6849 52.6698 49.0473 51.9454 48.7872L50.3479 47.8213C50.125 47.6913 49.8278 47.747 49.6606 47.9514C49.6606 47.9514 49.6049 48.0257 49.4935 48.1371C48.7319 47.617 47.506 46.0939 47.3759 45.6109C47.2645 45.1837 47.2831 42.8061 47.3388 41.153C48.2861 40.0942 48.7319 39.1655 48.7691 39.0912C48.7876 39.0726 48.7876 39.054 48.7876 39.0169L49.735 35.7663C50.2922 35.5805 50.7751 35.1533 51.1652 34.5404C51.5181 33.9645 51.8153 33.1101 51.5924 32.4414C51.5553 32.2185 51.8896 31.4384 52.1311 30.9183C52.5769 29.8967 53.097 28.745 52.9298 27.8534C52.8184 27.2405 52.4654 26.9618 52.2054 26.8504C52.094 26.7947 51.9825 26.7761 51.8711 26.7575C51.9454 24.9 52.1311 17.6001 50.1622 14.331C47.9146 10.6346 43.1595 9 39.4259 9C35.6924 9 30.9372 10.6346 28.7268 14.3124C26.7579 17.5816 26.9251 24.8815 27.018 26.7389C26.9065 26.7575 26.7951 26.7947 26.6836 26.8318C26.4236 26.9618 26.0706 27.2219 25.9592 27.8349C25.792 28.7264 26.2935 29.8781 26.7579 30.8997C26.9808 31.4198 27.3337 32.2185 27.2966 32.4228C27.0923 33.1101 27.3709 33.9645 27.7238 34.5404C28.0953 35.1533 28.5968 35.5805 29.1541 35.7663L30.1014 39.0169C30.1014 39.0355 30.12 39.054 30.12 39.0912C30.1571 39.1841 30.6586 40.2242 31.7545 41.3759C31.4759 43.9764 31.0673 47.8585 31.0115 48.1557C30.9558 48.3414 30.4543 48.9173 29.9342 49.4002C29.767 49.233 29.6742 49.1402 29.6742 49.1402C29.4884 48.9358 29.1726 48.8987 28.9497 49.0659L27.8538 49.9203C26.8879 50.1618 19.8667 51.8892 15.6502 52.8923C14.3871 53.208 13.3098 54.0996 12.8082 55.307C11.7123 57.8889 9.65052 63.0341 8.27598 68.5137C6.43708 75.795 5.61978 81.1445 5.60121 81.1817C5.48976 81.3117 5.45261 81.4975 5.48976 81.6646C5.50834 81.7389 6.08416 83.6521 8.10881 84.3208C8.03451 86.1226 8.16453 88.3701 8.7775 90.952C10.4678 98.0848 13.2355 99.6079 13.3655 99.6822C13.4398 99.7194 13.5327 99.7379 13.607 99.7379C13.6255 99.7379 13.6441 99.7379 13.6813 99.7379C14.5914 99.6265 16.6161 99.3479 17.8792 99.2178C17.3962 103.044 15.7245 116.66 16.2074 118.22L17.2105 117.904C17.2105 117.886 17.2105 117.867 17.1919 117.848C16.8947 116.548 18.0835 105.868 18.9379 99.1435C19.8295 99.125 27.2966 98.8649 30.5658 97.4904C31.1416 97.2489 32.1818 96.7474 33.482 96.0973C34.3364 95.67 35.3395 95.1871 36.454 94.6484C37.0484 94.9828 43.6238 98.5491 48.7505 99.5893C51.1281 100.072 53.7285 100.369 56.0132 100.648C58.5951 100.945 61.0099 101.242 61.8086 101.651C61.8829 101.688 61.9757 101.744 62.0872 101.8C62.2172 102.598 62.7002 105.7 62.6073 107.354C62.4958 109.23 62.7373 116.251 62.9974 118.536C63.0159 118.721 63.0345 118.87 63.0531 118.981L64.0933 118.796C63.8147 117.291 63.536 109.341 63.6475 107.409C63.7218 105.96 63.406 103.583 63.2203 102.301C63.7404 102.487 64.3347 102.636 64.9106 102.636C65.6907 102.636 66.4708 102.376 67.0467 101.595C68.124 100.128 71.6718 93.3668 71.7089 84.4509C72.6563 83.9493 73.2321 83.3364 73.4364 82.6491C73.6779 81.9061 73.3621 81.3303 73.325 81.2746Z"
        fill="white"
      />
      <path
        d="M57.276 80.8101C57.2945 79.287 57.4431 71.5599 58.4462 69.3309L57.4803 68.9037C56.3844 71.3741 56.2358 79.287 56.2172 80.8659C51.3506 79.4727 46.7626 80.5687 43.9764 81.2188C43.2148 81.4045 42.5462 81.5531 42.0632 81.6274C40.8187 81.7946 37.9768 82.3518 35.2463 82.8719C33.5745 83.2063 31.8656 83.5221 30.714 83.7264C25.2344 81.3116 21.8167 81.033 20.3493 81.0702C20.3493 81.0516 20.3493 81.0516 20.3493 81.033C20.3864 80.8473 21.0365 72.0985 20.5164 68.4207L19.4762 68.5693C19.8477 71.1698 19.5877 78.5069 19.4205 80.0114C14.3682 76.5379 9.5573 78.4883 7.14258 79.9743C7.60695 78.674 8.27564 72.3586 9.18581 68.7737C10.5418 63.3684 12.5664 58.2789 13.6623 55.7341C14.0524 54.8426 14.8325 54.1553 15.7984 53.9324C20.3121 52.8365 28.0392 50.9418 28.1135 50.9233C28.1878 50.9047 28.2621 50.8675 28.3179 50.8304L29.1351 50.1989C30.1939 51.2205 33.6302 54.0067 39.3327 54.0067C45.2024 54.0067 49.0102 50.1803 50.069 48.9543L51.332 49.7159C51.3692 49.7345 51.4063 49.7531 51.4435 49.7716C51.5364 49.8088 60.0436 52.6322 64.5944 53.8952C65.4675 54.1367 66.2105 54.7311 66.6562 55.5298C67.845 57.6845 69.9068 62.1239 70.3898 67.6592C70.8356 72.7115 71.4671 79.5099 71.8943 80.773C69.3682 80.1786 62.7741 78.9341 57.276 80.8101Z"
        fill={shirtColorHex(shirtColor)}
        stroke={shirtColorHex(shirtColor)}
      />
      <path
        d="M73.3247 81.2746C73.3061 81.256 72.2102 75.182 71.5415 67.5664C71.04 61.8267 68.9039 57.2388 67.678 55.0284C67.1021 53.9882 66.1177 53.208 64.9846 52.8923C60.6381 51.6849 52.6695 49.0473 51.9451 48.7872L50.3477 47.8213C50.1248 47.6913 49.8276 47.747 49.6604 47.9514C49.6604 47.9514 49.6047 48.0257 49.4932 48.1371C48.7317 47.617 47.5057 46.0939 47.3757 45.6109C47.2643 45.1837 47.2828 42.8061 47.3386 41.153C48.2859 40.0942 48.7317 39.1655 48.7688 39.0912C48.7874 39.0726 48.7874 39.054 48.7874 39.0169L49.7347 35.7663C50.292 35.5805 50.7749 35.1533 51.165 34.5404C51.5179 33.9645 51.8151 33.1101 51.5922 32.4414C51.555 32.2185 51.8894 31.4384 52.1309 30.9183C52.5766 29.8967 53.0967 28.745 52.9296 27.8534C52.8181 27.2405 52.4652 26.9618 52.2052 26.8504C52.0937 26.7947 51.9823 26.7761 51.8708 26.7575C51.9451 24.9 52.1309 17.6001 50.1619 14.331C47.9144 10.6346 43.1592 9 39.4257 9C35.6922 9 30.937 10.6346 28.7266 14.3124C26.7577 17.5816 26.9248 24.8815 27.0177 26.7389C26.9063 26.7575 26.7948 26.7947 26.6834 26.8318C26.4233 26.9618 26.0704 27.2219 25.959 27.8349C25.7918 28.7264 26.2933 29.8781 26.7577 30.8997C26.9806 31.4198 27.3335 32.2185 27.2963 32.4228C27.092 33.1101 27.3706 33.9645 27.7236 34.5404C28.0951 35.1533 28.5966 35.5805 29.1538 35.7663L30.1011 39.0169C30.1011 39.0355 30.1197 39.054 30.1197 39.0912C30.1569 39.1841 30.6584 40.2242 31.7543 41.3759C31.4757 43.9764 31.067 47.8585 31.0113 48.1557C30.9556 48.3414 30.4541 48.9173 29.934 49.4002C29.7668 49.233 29.6739 49.1402 29.6739 49.1402C29.4882 48.9358 29.1724 48.8987 28.9495 49.0659L27.8536 49.9203C26.8877 50.1618 19.8664 51.8892 15.6499 52.9108C14.3868 53.2266 13.3095 54.1182 12.808 55.3256C11.7121 57.9075 9.65027 63.0527 8.27574 68.5323C6.43683 75.8136 5.61954 81.1631 5.60097 81.2003C5.48952 81.3303 5.45237 81.516 5.48952 81.6832C5.50809 81.7575 6.08391 83.6707 8.10856 84.3394C8.03427 86.1412 8.16429 88.3887 8.77726 90.9706C10.4676 98.1033 13.2352 99.6265 13.3652 99.7008C13.4395 99.7379 13.5324 99.7565 13.6067 99.7565C13.6253 99.7565 13.6439 99.7565 13.681 99.7565C14.5912 99.6451 16.6158 99.3664 17.8789 99.2364C17.396 103.063 15.7242 116.678 16.2072 118.238L17.2102 117.923C17.2102 117.904 17.2102 117.886 17.1916 117.867C16.8944 116.567 18.0832 105.886 18.9377 99.1621C19.8293 99.1435 27.2963 98.8835 30.5655 97.5089C31.1413 97.2675 32.1815 96.766 33.4818 96.1158C34.3362 95.6886 35.3392 95.2057 36.4537 94.667C37.0481 95.0014 43.6236 98.5677 48.7502 99.6079C51.1278 100.091 53.7283 100.388 56.013 100.667C58.5949 100.964 61.0096 101.261 61.8083 101.67C61.8826 101.707 61.9755 101.763 62.0869 101.818C62.217 102.617 62.6999 105.719 62.607 107.372C62.4956 109.248 62.7371 116.27 62.9971 118.554C63.0157 118.74 63.0343 118.889 63.0528 119L64.093 118.814C63.8144 117.31 63.5358 109.36 63.6472 107.428C63.7215 105.979 63.4058 103.601 63.22 102.32C63.7401 102.506 64.3345 102.654 64.9103 102.654C65.6905 102.654 66.4706 102.394 67.0464 101.614C68.1238 100.147 71.6716 93.3853 71.7087 84.4694C72.656 83.9679 73.2318 83.3549 73.4362 82.6677C73.6776 81.9061 73.3619 81.3303 73.3247 81.2746ZM72.4517 82.3891C72.3588 82.6863 72.1173 82.9649 71.7273 83.2435C71.7087 82.7606 71.6901 82.259 71.653 81.7575C72.0245 81.8504 72.3031 81.9061 72.4888 81.9618C72.4888 82.0733 72.5074 82.2219 72.4517 82.3891ZM57.3875 80.8102C57.4061 79.2871 57.5547 71.5599 58.5577 69.331L57.5919 68.9037C56.4959 71.3742 56.3473 79.2871 56.3288 80.8659C51.4622 79.4728 46.8742 80.5687 44.088 81.2188C43.3264 81.4046 42.6577 81.5532 42.1748 81.6275C40.9302 81.7947 38.0883 82.3519 35.3578 82.872C33.6861 83.2064 31.9772 83.5221 30.8256 83.7264C25.346 81.3117 21.9282 81.0331 20.4608 81.0702C20.4608 81.0517 20.4608 81.0517 20.4608 81.0331C20.498 80.8473 21.1481 72.0986 20.628 68.4208L19.5878 68.5694C19.9593 71.1699 19.6992 78.5069 19.5321 80.0115C14.4611 76.538 9.66885 78.5069 7.25412 79.9929C7.71849 78.6927 8.38719 72.3772 9.29735 68.7923C10.6533 63.387 12.678 58.2975 13.7739 55.7528C14.164 54.8612 14.9441 54.1739 15.91 53.951C20.4237 52.8551 28.1508 50.9605 28.2251 50.9419C28.2994 50.9233 28.3737 50.8862 28.4294 50.849L29.2467 50.2175C30.3055 51.2391 33.7418 54.0253 39.4443 54.0253C45.3139 54.0253 49.1217 50.1989 50.1805 48.973L51.4436 49.7346C51.4807 49.7531 51.5179 49.7717 51.555 49.7903C51.6479 49.8274 60.1552 52.6508 64.706 53.9139C65.579 54.1554 66.322 54.7497 66.7678 55.5485C67.9566 57.7031 70.0184 62.1425 70.5013 67.6778C70.9471 72.7302 71.5787 79.5285 72.0059 80.7916C69.4797 80.1787 62.8857 78.9341 57.3875 80.8102ZM20.1079 83.9493C19.9964 83.2992 20.0707 82.6305 20.1636 82.129C21.0924 82.0733 23.9343 82.129 28.6894 83.9865C26.8877 84.0794 23.3956 84.2094 21.8725 84.0422C21.4081 83.9865 20.7766 83.9679 20.1079 83.9493ZM8.18286 83.2621C7.2727 82.872 6.80833 82.1104 6.62258 81.7018C6.99408 81.4046 7.71849 80.8474 8.70296 80.3458C8.51721 81.0145 8.31289 82.0176 8.18286 83.2621ZM29.5439 33.463L28.5408 33.7602L28.6151 34.0203C28.6151 34.0203 28.6152 34.0017 28.5966 34.0017C28.2808 33.5002 28.2065 32.9801 28.2622 32.7572C28.4294 32.1999 28.0951 31.4384 27.6864 30.491C27.3149 29.6552 26.8505 28.615 26.962 28.0392C26.9806 27.9092 27.0363 27.8163 27.092 27.7977C27.3149 27.6863 27.8907 27.9092 28.2065 28.0949L28.4666 27.6491L28.7452 27.2033C28.6523 27.1476 28.3923 26.999 28.0579 26.8875C28.0022 25.4759 27.7421 17.9531 29.6182 14.8696C31.6057 11.5633 36.1008 10.0588 39.4257 10.0588C42.7506 10.0588 47.2271 11.5633 49.2332 14.8696C51.0907 17.9531 50.8492 25.4759 50.7935 26.8875C50.4591 26.999 50.1991 27.1476 50.1062 27.2033L50.6449 28.0949C50.9606 27.9092 51.5365 27.7048 51.7594 27.7977C51.8151 27.8163 51.8522 27.9092 51.8894 28.0392C52.0008 28.615 51.5365 29.6552 51.165 30.491C50.7377 31.4384 50.4034 32.1999 50.5891 32.7572C50.6634 32.9801 50.5706 33.5002 50.2548 34.0017C50.2548 34.0017 50.2548 34.0203 50.2362 34.0203L50.3105 33.7602L49.3075 33.463L47.7658 38.6825C47.5429 39.1283 45.3139 43.4748 39.4257 43.4748C33.5375 43.4748 31.2899 39.1283 31.0856 38.6825L29.5439 33.463ZM32.7016 42.2489C34.169 43.4377 36.3608 44.515 39.4257 44.515C42.5463 44.515 44.7567 43.4005 46.2241 42.1746C46.2055 43.4748 46.1869 45.3509 46.3355 45.8896C46.5398 46.5954 47.7286 48.1371 48.7502 48.8987C47.2828 50.3475 43.9951 52.9294 39.4257 52.9294C35.1349 52.9294 32.2001 51.202 30.7327 50.0689C31.2342 49.586 31.94 48.843 32.0329 48.3414C32.1072 47.9699 32.5159 44.0878 32.7016 42.2489ZM33.036 95.1871C31.7357 95.8186 30.7141 96.3202 30.1754 96.5616C27.1849 97.8247 19.8107 98.1219 18.8634 98.1219C18.1204 98.1219 14.684 98.5677 13.7367 98.6977C13.2166 98.3077 11.1548 96.4873 9.79887 90.7477C8.36861 84.6738 9.68742 80.5501 9.98462 79.77C12.4179 78.8413 15.7985 78.5069 19.3463 81.1445C19.1606 81.7018 18.9005 82.8163 19.0491 83.9679C17.2474 83.9679 15.4456 84.0422 15.3156 84.0608L15.3527 85.101C15.3899 85.101 19.9407 84.9152 21.761 85.101C22.3369 85.1567 23.1356 85.1753 24.0086 85.1753C26.1633 85.1753 28.8009 85.0453 29.6368 84.9895C30.2683 84.9524 32.7202 84.4694 35.5621 83.9122C38.2741 83.3921 41.0974 82.8349 42.3234 82.6677C42.862 82.5934 43.5493 82.4262 44.348 82.2405C47.3014 81.5346 51.7222 80.4944 56.4774 81.999C56.886 83.1135 57.629 85.4539 57.5361 86.7541C50.7749 86.4755 38.8499 92.3266 33.036 95.1871ZM56.143 99.6265C53.8769 99.3664 51.3136 99.0507 48.9731 98.5863C44.831 97.7504 39.5186 95.0942 37.6425 94.1098C43.9765 91.1192 52.8181 87.3485 57.9262 87.8129C58.0376 87.8315 58.1491 87.7943 58.242 87.7386C58.2605 87.7386 59.8022 86.8284 61.3997 87.0885L61.5669 86.0483C60.3966 85.8625 59.3007 86.1412 58.5763 86.4198C58.5577 84.8781 57.889 82.8163 57.5361 81.8504C62.0684 80.2344 67.5851 80.9402 70.5571 81.516C71.3929 91.4907 67.2879 99.4965 66.2106 100.964C65.5419 101.874 64.3345 101.8 62.3098 100.722C61.344 100.239 59.0593 99.9794 56.143 99.6265Z"
        fill="black"
      />
    </g>
  </svg>
);

export const FacebookIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="10" cy="10" r="10" fill="#3B5998" />
    <path
      d="M8.89211 10.537C8.85731 10.537 8.09165 10.537 7.74362 10.537C7.558 10.537 7.5 10.4745 7.5 10.318C7.5 9.90084 7.5 9.47322 7.5 9.05603C7.5 8.88916 7.56961 8.83701 7.74362 8.83701H8.89211C8.89211 8.80572 8.89211 8.20079 8.89211 7.91919C8.89211 7.502 8.97332 7.10568 9.20534 6.74064C9.44896 6.36517 9.79698 6.11485 10.2378 5.96884C10.5278 5.87497 10.8179 5.83325 11.1311 5.83325H12.268C12.4304 5.83325 12.5 5.89583 12.5 6.04185V7.23083C12.5 7.37685 12.4304 7.43943 12.268 7.43943C11.9548 7.43943 11.6415 7.43943 11.3283 7.44986C11.0151 7.44986 10.8527 7.58544 10.8527 7.87747C10.8411 8.19037 10.8527 8.49283 10.8527 8.81615H12.1984C12.384 8.81615 12.4536 8.87873 12.4536 9.0456V10.3076C12.4536 10.4745 12.3956 10.5266 12.1984 10.5266C11.7807 10.5266 10.8875 10.5266 10.8527 10.5266V13.9267C10.8527 14.104 10.7947 14.1666 10.5858 14.1666C10.0986 14.1666 9.62297 14.1666 9.13573 14.1666C8.96172 14.1666 8.89211 14.104 8.89211 13.9476C8.89211 12.8524 8.89211 10.5683 8.89211 10.537Z"
      fill="white"
    />
  </svg>
);

export const GodaddyIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="10" cy="10" r="9.79167" fill="white" />
    <circle cx="10" cy="10" r="9.79167" fill="#1BDBDB" />
    <circle
      cx="10"
      cy="10"
      r="9.79167"
      stroke="#E0E2E6"
      strokeWidth="0.416667"
    />
    <path
      d="M14.351 5.50272C13.1039 4.70058 11.4622 4.89169 9.99787 5.83887C8.53817 4.89169 6.89537 4.70058 5.64979 5.50272C3.67945 6.77034 3.43989 10.0349 5.11524 12.7948C6.35014 14.8295 8.28132 16.0217 10.0004 15.9997C11.7195 16.0217 13.6507 14.8295 14.8856 12.7948C16.5589 10.0349 16.3214 6.77034 14.351 5.50272ZM6.02107 12.212C5.67562 11.6483 5.41498 11.0339 5.24799 10.3899C5.09963 9.83089 5.0429 9.25029 5.08015 8.67203C5.15492 7.65207 5.55824 6.85778 6.21536 6.43524C6.87248 6.0127 7.74118 5.98704 8.66634 6.36508C8.80519 6.42215 8.94251 6.48812 9.07882 6.5609C8.56471 7.04348 8.11768 7.59672 7.75034 8.20498C6.73312 9.88048 6.42287 11.745 6.77788 13.2315C6.49481 12.9171 6.2414 12.5758 6.02107 12.212ZM14.7534 10.3894C14.5861 11.0333 14.3255 11.6476 13.9803 12.2115C13.7598 12.5758 13.5064 12.9178 13.2235 13.233C13.5408 11.9005 13.3252 10.2679 12.547 8.73695C12.5346 8.71174 12.5172 8.68949 12.4959 8.67164C12.4745 8.65379 12.4498 8.64075 12.4233 8.63335C12.3968 8.62594 12.3691 8.62435 12.342 8.62867C12.3148 8.63299 12.2889 8.64312 12.2658 8.65841L9.8397 10.2187C9.81757 10.2329 9.79838 10.2515 9.78323 10.2734C9.76808 10.2952 9.75727 10.3199 9.75141 10.3461C9.74555 10.3723 9.74476 10.3994 9.74909 10.4258C9.75342 10.4523 9.76278 10.4777 9.77663 10.5004L10.1327 11.0863C10.1464 11.1091 10.1644 11.1288 10.1856 11.1444C10.2068 11.16 10.2307 11.1711 10.2561 11.1772C10.2815 11.1832 10.3078 11.184 10.3335 11.1796C10.3591 11.1751 10.3837 11.1655 10.4058 11.1512L11.9784 10.1397C12.0293 10.2967 12.0801 10.4538 12.1183 10.6109C12.2666 11.1692 12.3235 11.7491 12.2866 12.3267C12.2118 13.3461 11.8085 14.1404 11.1514 14.5635C10.8125 14.7768 10.4241 14.8926 10.0269 14.8986H9.97651C9.57927 14.8927 9.19087 14.7769 8.85198 14.5635C8.19435 14.1404 7.79103 13.3461 7.71626 12.3267C7.67924 11.7484 7.73596 11.1679 7.8841 10.6088C8.22496 9.31532 8.92956 8.15442 9.90938 7.27194C10.3319 6.89013 10.8132 6.58331 11.3335 6.36403C12.2561 5.986 13.1263 6.01113 13.7839 6.43419C14.4416 6.85725 14.8444 7.65102 14.9192 8.67098C14.9569 9.24929 14.9009 9.83004 14.7534 10.3894Z"
      fill="white"
    />
  </svg>
);

export const ShopifyIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="10" cy="10" r="9.79167" fill="white" />
    <circle cx="10" cy="10" r="9.79167" fill="#95BF47" />
    <circle
      cx="10"
      cy="10"
      r="9.79167"
      stroke="#E0E2E6"
      strokeWidth="0.416667"
    />
    <path
      d="M13 6.26166L12.2903 8.28794C12.2903 8.28794 11.7097 7.97133 10.9355 7.97133C9.83871 7.97133 9.77419 8.60455 9.77419 8.79451C9.77419 9.68101 12.2258 10.0609 12.2258 12.2139C12.2258 13.9235 11.129 15 9.64516 15C7.90323 15 7 13.9235 7 13.9235L7.45161 12.4038C7.45161 12.4038 8.35484 13.1637 9.12903 13.1637C9.64516 13.1637 9.83871 12.7838 9.83871 12.4671C9.83871 11.264 7.83871 11.2007 7.83871 9.23776C7.83871 7.59141 9.06452 6.00837 11.4516 6.00837C12.5484 5.94505 13 6.26166 13 6.26166Z"
      fill="white"
    />
  </svg>
);
